import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList, Plus, Eye,
  Calendar, Target, CheckCircle, Archive, AlertTriangle, Download
} from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './PlanList.css';

const STATUS_CSS = {
  draft:     'plan-status-draft',
  active:    'plan-status-active',
  completed: 'plan-status-completed',
  archived:  'plan-status-archived',
};

const TYPE_CSS = {
  annual:    'plan-type-annual',
  quarterly: 'plan-type-quarterly',
  monthly:   'plan-type-monthly',
};

function PlanList() {
  const navigate = useNavigate();
  const { t } = useTranslation(['plans', 'common']);
  const [plans, setPlans] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Фильтры
  const [filters, setFilters] = useState({
    plan_type: '',
    status: '',
    department: '',
    year: '',
    search: '',
  });

  // Статистика
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    avgCompletion: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userRes, deptsRes] = await Promise.all([
        api.getCurrentUser(),
        api.getDepartments(),
      ]);
      setUser(userRes.data);
      setDepartments(deptsRes.data.results || deptsRes.data);
      await fetchPlans();
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const params = {};
      if (filters.plan_type) params.plan_type = filters.plan_type;
      if (filters.status) params.status = filters.status;
      if (filters.department) params.department = filters.department;
      if (filters.year) params.year = filters.year;
      if (filters.search) params.search = filters.search;

      const [plansRes, statsRes] = await Promise.all([
        api.getPlans(params),
        api.getPlanStatistics(params),
      ]);
      const plansData = plansRes.data.results || plansRes.data;
      setPlans(plansData);
      setStats({
        total: statsRes.data.total_plans || 0,
        active: statsRes.data.by_status?.active || 0,
        completed: statsRes.data.by_status?.completed || 0,
        avgCompletion: statsRes.data.avg_completion || 0,
      });
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const getProgressColor = (pct) => {
    if (pct >= 80) return 'green';
    if (pct >= 50) return 'yellow';
    return 'red';
  };

  const leadershipRoles = ['admin', 'director', 'deputy_director'];
  const isAdmin = user?.is_staff || user?.is_superuser || leadershipRoles.includes(user?.role);
  const dashboardPath = isAdmin ? '/dashboard/admin' : '/dashboard/department';

  // Генерируем список годов (текущий ± 2)
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  if (loading) {
    return (
      <div className="plan-list-page">
        <div className="loading-spinner">{t('common:table.loading')}</div>
      </div>
    );
  }

  return (
    <div className="plan-list-page">
      {/* Header */}
      <div className="plan-list-header">
        <h1>
          <ClipboardList size={24} />
          {t('plans:pageTitles.planning')}
        </h1>
        <div className="header-actions">
          <button className="btn-export" onClick={async () => {
            try { const r = await api.exportPlansExcel(); downloadBlob(r, 'Планы.xlsx'); } catch {}
          }}><Download size={16} /> {t('plans:excelExport')}</button>
          {isAdmin && (
            <button className="btn-primary" onClick={() => navigate('/plans/create')}>
              <Plus size={18} />
              {t('plans:actions.createPlan')}
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="plan-stats">
        <div className="plan-stat-card blue">
          <div className="stat-icon"><ClipboardList size={24} /></div>
          <div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">{t('plans:stats.totalPlans')}</div>
          </div>
        </div>
        <div className="plan-stat-card green">
          <div className="stat-icon"><Target size={24} /></div>
          <div>
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">{t('plans:stats.active')}</div>
          </div>
        </div>
        <div className="plan-stat-card orange">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div>
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">{t('plans:stats.completed')}</div>
          </div>
        </div>
        <div className="plan-stat-card purple">
          <div className="stat-icon"><Calendar size={24} /></div>
          <div>
            <div className="stat-value">{stats.avgCompletion}%</div>
            <div className="stat-label">{t('plans:stats.avgCompletion')}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('plans:form.search')}</label>
            <input
              type="text"
              className="filter-control"
              placeholder={t('plans:form.planTitlePlaceholder')}
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          <div className="filter-group">
            <label>{t('plans:form.type')}</label>
            <select
              className="filter-control"
              value={filters.plan_type}
              onChange={e => setFilters({ ...filters, plan_type: e.target.value })}
            >
              <option value="">{t('plans:form.allTypes')}</option>
              <option value="annual">{t('plans:typeLabels.annual')}</option>
              <option value="quarterly">{t('plans:typeLabels.quarterly')}</option>
              <option value="monthly">{t('plans:typeLabels.monthly')}</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{t('common:table.status')}</label>
            <select
              className="filter-control"
              value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">{t('plans:form.allStatuses')}</option>
              <option value="draft">{t('plans:statusLabels.draft')}</option>
              <option value="active">{t('plans:statusLabels.active')}</option>
              <option value="completed">{t('plans:statusLabels.completed')}</option>
              <option value="archived">{t('plans:statusLabels.archived')}</option>
            </select>
          </div>
          {isAdmin && (
            <div className="filter-group">
              <label>{t('common:table.department')}</label>
              <select
                className="filter-control"
                value={filters.department}
                onChange={e => setFilters({ ...filters, department: e.target.value })}
              >
                <option value="">{t('plans:form.allDepartments')}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name_ru}</option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>{t('plans:form.year')}</label>
            <select
              className="filter-control"
              value={filters.year}
              onChange={e => setFilters({ ...filters, year: e.target.value })}
            >
              <option value="">{t('plans:form.allYears')}</option>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="plans-table-card">
        <h2>
          <ClipboardList size={20} />
          Планы ({plans.length})
        </h2>

        {plans.length === 0 ? (
          <div className="empty-state">
            <Archive size={48} />
            <h3>{t('plans:noPlans')}</h3>
            <p>{t('plans:noPlansForFilters')}</p>
          </div>
        ) : (
          <table className="plans-table">
            <thead>
              <tr>
                <th>{t('plans:table.title')}</th>
                <th>{t('plans:table.type')}</th>
                <th>{t('plans:table.department')}</th>
                <th>{t('plans:table.year')}</th>
                <th>{t('plans:table.period')}</th>
                <th>{t('plans:table.status')}</th>
                <th>{t('plans:table.completion')}</th>
                <th>{t('plans:table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(plan => {
                const statusCss = STATUS_CSS[plan.status] || STATUS_CSS.draft;
                const statusLabel = t(`plans:statusLabels.${plan.status}`, plan.status);
                const typeCss = TYPE_CSS[plan.plan_type] || '';
                const typeLabel = t(`plans:typeLabels.${plan.plan_type}`, plan.plan_type);
                const pct = plan.completion_percentage || 0;
                const color = getProgressColor(pct);

                return (
                  <tr key={plan.id}>
                    <td>
                      <span
                        className="plan-title-link"
                        onClick={() => navigate(`/plans/${plan.id}`)}
                      >
                        {plan.title}
                      </span>
                      {plan.is_overdue && (
                        <span className="overdue-indicator" style={{ marginLeft: '0.5rem' }}>
                          <AlertTriangle size={14} />
                          {t('plans:overdue')}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`plan-type-badge ${typeCss}`}>
                        {typeLabel}
                      </span>
                    </td>
                    <td>{plan.department_name}</td>
                    <td>{plan.year}</td>
                    <td>
                      {plan.start_date && plan.end_date
                        ? `${new Date(plan.start_date).toLocaleDateString(getDateLocale())} — ${new Date(plan.end_date).toLocaleDateString(getDateLocale())}`
                        : '—'
                      }
                    </td>
                    <td>
                      <span className={`plan-status-badge ${statusCss}`}>
                        {statusLabel}
                      </span>
                    </td>
                    <td>
                      <div className="progress-bar-container">
                        <div className="progress-bar">
                          <div
                            className={`progress-bar-fill ${color}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="progress-text">{pct}%</span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-secondary"
                          onClick={() => navigate(`/plans/${plan.id}`)}
                        >
                          <Eye size={14} />
                          {t('plans:actions.view')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default PlanList;
